import dayjs, { type Dayjs } from 'dayjs';
import { computed, ref, type ComputedRef } from 'vue';

/**
 * Single sanctioned frontend "now" for the migrated paths (PRD #918).
 *
 * `now()` = `Date.now()` + active offset. The offset is the challenge virtual
 * clock — server-authoritative, mirrored from the `/challenge/progress` poll
 * via `setChallengeClockOffsetMs`, with an optimistic local bump on advance
 * (`bumpChallengeClockOffsetMsOptimistic`). Outside challenge mode the offset
 * stays 0 and `now()` is identity, so production paths are untouched.
 *
 * The offset is anchored, not frozen — the clock keeps ticking because
 * `Date.now()` ticks. A 1 s reactive ticker is touched inside `nowMs()` so
 * computeds reading the clock re-evaluate as time passes (and immediately when
 * the offset changes).
 *
 * Lives in `@therame/ui` (domain-free, depends only on `dayjs` + `vue`) so both
 * the PVS and the embedded booking widget read "now" through one composable
 * (PRD #1358). The challenge-clock offset setters below are still driven from
 * the app side — the UI package does not import the app, so no cycle is created.
 */

const offsetMs = ref(0);
const tick = ref(0);

const TICK_INTERVAL_MS = 1000;

let tickHandle: ReturnType<typeof setInterval> | null = null;

function ensureTicker(): void {
  if (tickHandle !== null) return;
  if (typeof setInterval === 'undefined') return;
  tickHandle = setInterval(() => {
    tick.value = (tick.value + 1) % Number.MAX_SAFE_INTEGER;
  }, TICK_INTERVAL_MS);
}

/**
 * Mirror the server-authoritative offset onto the frontend clock. Called by
 * the challenge progress poll. Negative values are clamped to 0 — the server
 * enforces forward-only, but defending here keeps the clock provably
 * non-negative even if the wire ever lies.
 */
export function setChallengeClockOffsetMs(ms: number): void {
  offsetMs.value = Math.max(0, ms);
}

/**
 * Optimistic local bump for the acting tab after pressing an "advance" button,
 * so the simulated-time label and gates move immediately without waiting for
 * the next poll. Forward-only.
 */
export function bumpChallengeClockOffsetMsOptimistic(deltaMs: number): void {
  if (deltaMs <= 0) return;
  offsetMs.value += deltaMs;
}

export interface UseSystemTimeReturn {
  /** Current virtual epoch ms. Reactive within Vue effects (computed/watch/render). */
  nowMs: () => number;
  /** Current virtual `Date` snapshot. Reactive within Vue effects. */
  now: () => Date;
  /** Current virtual `dayjs` snapshot. Reactive within Vue effects. */
  nowDayjs: () => Dayjs;
  /** Active offset in ms. Reactive. */
  offsetMs: ComputedRef<number>;
}

export function useSystemTime(): UseSystemTimeReturn {
  ensureTicker();

  const nowMs = (): number => {
    void tick.value;
    return Date.now() + offsetMs.value;
  };

  return {
    nowMs,
    now: () => new Date(nowMs()),
    nowDayjs: () => dayjs(nowMs()),
    offsetMs: computed(() => offsetMs.value),
  };
}
